// import * as React from "react";
// import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
// import { Button } from "@progress/kendo-react-buttons";
// import { Label, Error, Hint } from "@progress/kendo-react-labels";
// import { Dialog } from "@progress/kendo-react-dialogs";
// import { Alert } from "./alert";
// import ErrorHandler from "../../../../apps/tenant/src/utils/errorConfig";
// import { useNavigate } from "react-router-dom";
// import { useIntl } from "react-intl";

// export const FormFileUpload = (fieldRenderProps: FieldRenderProps) => {
//   const {
//     value,
//     id,
//     label,
//     hint,
//     validationMessage,
//     touched,
//     optional,
//     disabled,
//     onChange,
//     accountId,
//     buttonText,
//     infoText,
//     selectedFileName,
//     target,
//     ...others
//   } = fieldRenderProps;

//   const hiddenFileInput = React.useRef<HTMLInputElement>(null);
//   const showValidationMessage = touched && validationMessage;
//   const showHint = !showValidationMessage && hint;
//   const labelId = `${id}_label`;
//   const hintId = showHint ? `${id}_hint` : "";
//   const errorId = showValidationMessage ? `${id}_error` : "";

//   const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
//     event.preventDefault();
//     hiddenFileInput.current?.click();
//   };

//   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     if (event.target.files && event.target.files[0]) {
//       const file = event.target.files[0];
//       const fileName = file.name;

//       console.log("File to upload", file);
//       const url = `Customer-Info/acct-${accountId}/${fileName}`;
//       console.log("File selected:", fileName, "File URL:", url);
//       onChange({ target: file }); // Passing the file content as target
//     }
//   };
//   return (
//     <FieldWrapper>
//       <Label
//         id={labelId}
//         editorId={id}
//         editorValid={!showValidationMessage}
//         editorDisabled={disabled}
//         optional={optional}
//       >
//         {label}
//       </Label>
//       <div>
//         <Button
//           themeColor="primary"
//           className="font-bold"
//           disabled={disabled}
//           onClick={handleButtonClick}
//         >
//           {/* Upload Customer Logo */}
//           {buttonText}
//         </Button>
//         <input
//           type="file"
//           id={id}
//           className="hidden"
//           ref={hiddenFileInput}
//           onChange={handleFileChange}
//           accept=".png,.jpeg,.jpg"
//           disabled={disabled}
//           {...others}
//         />
//       </div>
//       {value && (
//         <div className="mt-2">
//           <strong>
//             {infoText}
//           </strong>
//           {selectedFileName}
//         </div>
//       )}
//       {showHint && <Hint id={hintId}>{hint}</Hint>}
//       {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
//     </FieldWrapper>
//   );
// };


//aspect ratio for signature met and suitable error dialog on upload of incorrect signatures
import * as React from "react";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Label, Error, Hint } from "@progress/kendo-react-labels";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Alert } from "./alert";
import { useIntl } from "react-intl";

interface FormFileUploadProps extends FieldRenderProps {
  accountId?: string;
  buttonText?: string;
  infoText?: string;
  selectedFileName?: string;
  target?: any;
  // Dimension validation props
  validateDimensions?: boolean; // Enable/disable dimension validation
  minWidthHeightRatio?: number; // Minimum ratio of width/height
  dimensionErrorMessage?: string; // Custom error message for dialog
}

export const FormFileUpload = (fieldRenderProps: FormFileUploadProps) => {
  const {
    value,
    id,
    label,
    hint,
    validationMessage,
    touched,
    optional,
    disabled,
    onChange,
    accountId,
    buttonText,
    infoText,
    selectedFileName,
    target,
    validateDimensions = false, // Default: no dimension validation
    minWidthHeightRatio = 1.2, // Default: width should be at least 20% greater than height
    // dimensionErrorMessage = "Images must be rectangular shape (width greater than height)",
    ...others
  } = fieldRenderProps;

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [dimensionError, setDimensionError] = React.useState<string>("");
  const [errorFlag, setErrorFlag] = React.useState<boolean>(false);

  const intl = useIntl()

  const showValidationMessage =
    touched && (validationMessage || dimensionError);
  const showHint = !showValidationMessage && hint;
  const labelId = `${id}_label`;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    hiddenFileInput.current?.click();
  };

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url); // Clean up the object URL

        const { width, height } = img;
        const aspectRatio = width / height;

        console.log(
          `Image dimensions: ${width}x${height}, Aspect ratio: ${aspectRatio.toFixed(
            2
          )}`
        );

        if (aspectRatio >= minWidthHeightRatio) {
          setDimensionError("");
          resolve(true);
        } else {
          const minRatioPercent = Math.round((minWidthHeightRatio - 1) * 100);
          setErrorFlag(true);
          // setDimensionError(
          //   `Image must be rectangular with width at least ${minRatioPercent}% greater than height. Current image is ${width}x${height} (ratio: ${aspectRatio.toFixed(
          //     2
          //   )})`
          // );
          resolve(false);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        setDimensionError("Unable to load image for dimension validation");
        resolve(false);
      };

      img.src = url;
    });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileName = file.name;

      console.log("File to upload", file);

      // Only validate dimensions if validateDimensions is true
      if (validateDimensions) {
        const isValidDimension = await validateImageDimensions(file);

        if (!isValidDimension) {
          // Clear the file input
          if (hiddenFileInput.current) {
            hiddenFileInput.current.value = "";
          }
          // Don't call onChange - just return to prevent upload
          return;
        }
      }

      const url = `Customer-Info/acct-${accountId}/${fileName}`;
      console.log("File selected:", fileName, "File URL:", url);
      onChange({ target: file }); // Passing the file content as target
    }
  };

  return (
    <FieldWrapper>
      {errorFlag && (
        <Dialog className="errorDialogue">
          <Alert
            // description={dimensionErrorMessage}
            description={intl.formatMessage({id:"formFileUpload.signatureValidation"})}
            buttonText={intl.formatMessage({id:"users.buttonText.Ok"})}
            icon="/errorShape.png"
            iconMsg={intl.formatMessage({id:"iconMsgError"})}
            backgroundColor="mk-red"
            onClick={() => {
              setErrorFlag(false);
            }}
          />
        </Dialog>
      )}
      <Label
        id={labelId}
        editorId={id}
        editorValid={!showValidationMessage}
        editorDisabled={disabled}
        optional={optional}
      >
        {label}
      </Label>
      <div>
        <Button
          themeColor="primary"
          className="font-bold"
          disabled={disabled}
          onClick={handleButtonClick}
        >
          {buttonText}
        </Button>
        <input
          type="file"
          id={id}
          className="hidden"
          ref={hiddenFileInput}
          onChange={handleFileChange}
          accept=".png,.jpeg,.jpg"
          disabled={disabled}
          {...others}
        />
      </div>
      {value && (
        <div className="mt-2">
          <strong>{infoText}</strong>
          {selectedFileName}
        </div>
      )}
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && (
        <Error id={errorId}>{dimensionError || validationMessage}</Error>
      )}
    </FieldWrapper>
  );
};
